import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import usePublishedFaqs from "../hooks/usePublishedFaqs";

const SITE_URL = "https://alarmita.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

type RouteMeta = {
  title: string;
  description: string;
  robots: string;
};

const routeMetadata: Record<string, RouteMeta> = {
  "/": {
    title: "Precios ADT Alarmas - Cotizá tu Alarma Ahora - Alarmita.com",
    description:
      "Alarmas ADT. Conocé promos, packs con cámaras y monitoreo bonificado hasta 6 meses.",
    robots: "index, follow"
  },
  "/cotizador": {
    title: "Cotizá tu Alarma ADT - Conocé Precios y Promos - Alarmita.com",
    description:
      "Completá el formulario y recibí una cotización personalizada para tu alarma ADT en minutos. Sin compromiso.",
    robots: "index, follow"
  },
  "/send": {
    title: "Solicitud recibida - ADT Alarmas",
    description:
      "Gracias por tu solicitud. Nuestro equipo te contactará en minutos con una cotización personalizada.",
    robots: "noindex, follow"
  }
};

const buildHomeStructuredData = (faqItems: Array<{ title: string; contentMd?: string }>) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      name: "Alarmita.com - ADT Agente Oficial",
      url: SITE_URL,
      telephone: "0810-220-3014",
      areaServed: "AR",
      priceRange: "$$",
      description:
        "Distribuidora oficial de alarmas ADT para hogar y comercio en Argentina."
    },
    {
      "@type": "Organization",
      name: "Alarmita.com",
      url: SITE_URL,
      description:
        "Agente oficial de ADT en Argentina con instalación y monitoreo de alarmas para hogar y comercio.",
      sameAs: ["https://wa.me/541126597907", "https://www.adt.com.ar/"]
    },
    {
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.title,
        acceptedAnswer: {
          "@type": "Answer",
          text: (item.contentMd || "").replace(/[*_`>#-]/g, " ").replace(/\s+/g, " ").trim()
        }
      }))
    },
    {
      "@type": "HowTo",
      name: "Cómo contratar tu alarma ADT",
      description: "Proceso simple para contratar y activar tu sistema de seguridad ADT.",
      step: [
        {
          "@type": "HowToStep",
          name: "Ingresás la información",
          text: "Completá tus datos para conocer las opciones de seguridad para tu hogar o comercio."
        },
        {
          "@type": "HowToStep",
          name: "Recibís la cotización",
          text: "Un especialista te envía una propuesta personalizada con precios y promociones."
        },
        {
          "@type": "HowToStep",
          name: "Instalamos tu alarma",
          text: "Coordinamos la instalación del sistema para dejar tu propiedad protegida."
        }
      ]
    }
  ]
});

const normalizePath = (path: string): string =>
  path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;

const SeoHead = (): JSX.Element => {
  const { items: publishedFaqGroups } = usePublishedFaqs();
  const location = useLocation();
  const path = normalizePath(location.pathname);
  const meta = routeMetadata[path] || routeMetadata["/"];
  const canonicalPath = path === "/" ? "" : path;
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const faqItems = publishedFaqGroups.flatMap((group) => (Array.isArray(group.faqItems) ? group.faqItems : []));
  const homeStructuredData = buildHomeStructuredData(faqItems);

  return (
    <Helmet prioritizeSeoTags>
      <html lang="es-AR" />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={meta.robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta
        property="og:image:alt"
        content="Promociones de alarmas ADT para hogar y comercio"
      />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="es_AR" />
      <meta property="og:site_name" content="Alarmita.com" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />

      {path === "/" ? <link rel="preload" as="image" href="/hero.avif" type="image/avif" /> : null}
      {path === "/" ? <link rel="preload" as="image" href="/hero.webp" type="image/webp" /> : null}

      {path === "/" ? (
        <script type="application/ld+json">
          {JSON.stringify(homeStructuredData)}
        </script>
      ) : null}
    </Helmet>
  );
};

export default SeoHead;
